import React from 'react';
import styled from 'styled-components';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 20px;
`;

const PaginationInfo = styled.div`
  color: #ccc;
  font-size: 14px;
  margin-right: 20px;
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  background: ${props => props.active ? '#00d4ff' : 'transparent'};
  color: ${props => props.active ? '#000' : props.disabled ? '#666' : '#00d4ff'};
  border: 1px solid ${props => props.disabled ? '#666' : '#00d4ff'};
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: 'Orbitron', monospace;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  min-width: 40px;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#0099cc' : 'rgba(0, 212, 255, 0.1)'};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
`;

interface MissionHistoryPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  hasMore: boolean;
  onPageChange: (page: number) => void;
}

const MissionHistoryPagination: React.FC<MissionHistoryPaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  hasMore,
  onPageChange
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Генерируем номера страниц для отображения
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Максимальное количество видимых номеров страниц

    if (totalPages <= maxVisible) {
      // Если страниц мало, показываем все
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Если страниц много, показываем с многоточиями
      pages.push(1);

      if (currentPage > 4) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <PaginationContainer>
      <PaginationInfo>
        Showing {startItem}-{endItem} of {totalCount} games
      </PaginationInfo>

      <PaginationButton
        disabled={currentPage <= 1}
        onClick={() => onPageChange(1)}
      >
        ««
      </PaginationButton>

      <PaginationButton
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        ‹
      </PaginationButton>

      <PageNumbers>
        {pageNumbers.map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} style={{ color: '#666', padding: '8px 4px' }}>
              ...
            </span>
          ) : (
            <PaginationButton
              key={page}
              active={page === currentPage}
              onClick={() => onPageChange(page as number)}
            >
              {page}
            </PaginationButton>
          )
        ))}
      </PageNumbers>

      <PaginationButton
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        ›
      </PaginationButton>

      <PaginationButton
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(totalPages)}
      >
        »»
      </PaginationButton>
    </PaginationContainer>
  );
};

export default MissionHistoryPagination;